import { useCallback, useMemo } from "react";
import { Tooltip } from "antd";
import {
  StyledDrawer,
  DrawerContent,
  DrawerSection,
  DrawerDescription,
  DrawerTitle,
  VideoContainer,
  VideoIframe,
  DownloadContainer,
  StatusChangeButton,
  ScrollingContainer,
} from "./styles";

import { HiOutlineExternalLink } from "react-icons/hi";
import { StatusBadge } from "../ChecklistTable/styles";
import { useDispatch, useSelector } from "react-redux";
import { solveChecklistStep } from "../../../redux/modules/checklist/actions";
import { useToast } from "../../../hooks/toast";

const ChecklistDrawer = ({ open, onClose, task, intl, onStatusChange, isHomepage, onStatusChanged }) => {
  const isPt = intl.locale.startsWith("pt");
  const dispatch = useDispatch();
  const { addToast } = useToast();
  const { event } = useSelector((state) => state.initialSettings);

  const TITLE_TOOLTIP_THRESHOLD = 67;

  const getTaskTitle = (task) => {
    if (!task) return "";

    return (
      task.title ||
      (isPt ? task.descriptionPt : task.descriptionEn) ||
      task.descriptionPt ||
      task.descriptionEn ||
      "-"
    );
  };

  const getTaskDescription = (task) => {
    if (!task) return "";

    return task.description || (isPt ? task.descriptionPt : task.descriptionEn);
  };

  const extractYoutubeId = (url) => {
    if (!url) return null;

    const regExp = /(?:youtube\.com\/.*v=|youtu\.be\/)([^&\n?#]+)/;
    const match = url.match(regExp);

    return match ? match[1] : null;
  };

  const youtubeLink = useMemo(() => {
    if (!task) return null;

    const link = task.link || (isPt ? task.linkPt : task.linkEn);
    return extractYoutubeId(link);
  }, [task, isPt]);

  const downloadLink = useMemo(() => {
    if (!task) return null;
    return (
      task?.mediaFileLink ||
      (isPt ? task.mediaFilePtLink : task.mediaFileEnLink)
    );
  }, [task, isPt]);

  const statusMap = {
    CONCLUDED: {
      label: intl.formatMessage({ id: "concluded" }),
      variant: "concluded",
    },
    PENDING: {
      label: intl.formatMessage({ id: "on_time" }),
      variant: "on_time",
    },
    ON_TRACK: {
      label: intl.formatMessage({ id: "on_time" }),
      variant: "on_time",
    },
    DELAYED: {
      label: intl.formatMessage({ id: "checklist.delayed" }),
      variant: "delayed",
    },
    OVERDUE: {
      label: intl.formatMessage({ id: "checklist.delayed" }),
      variant: "delayed",
    },
  };

  const normalized = task?.status ? task.status.toUpperCase() : null;

  const mappedStatus = statusMap[normalized] || {
    label: task?.status || "-",
    variant: "default",
  };

  const getHeaderBg = (status) => {
    const normalized = (status || "").trim().toUpperCase();

    switch (normalized) {
      case "COMPLETED":
      case "CONCLUDED":
        return "rgba(217, 247, 190, 1)";

      case "DELAYED":
        return "rgba(255, 204, 199, 1)";

      case "PENDING":
      default:
        return "transparent";
    }
  };

  const callBackMessage = useCallback(
    (message) =>
      addToast({
        type: "error",
        title: intl.formatMessage({
          id: "error",
        }),
        description: intl.formatMessage({
          id: "api.error." + message,
        }),
      }),
    [addToast, intl]
  );

  const solveTask = async () => {
    const solved = task.status.toUpperCase() === "CONCLUDED" ? false : true;

    const updatedTask = {
      ...task,
      status: solved ? "CONCLUDED" : "PENDING",
    };

    if (!isHomepage) {
      onStatusChange(updatedTask);
    }

    try {
      await dispatch(
        solveChecklistStep({
          eventChecklistId: task.eventChecklistId,
          userChecklistId: task.userChecklistId,
          solved,
          selectedEvent: event.value,
          callbackError: callBackMessage,
          callbackSuccess: isHomepage ? onStatusChanged : undefined,
        })
      );
    } catch (error) {
      console.error(error);
    }

    onClose();
  };

  return (
    <StyledDrawer
      title={
        <Tooltip
          title={
            getTaskTitle(task).length > TITLE_TOOLTIP_THRESHOLD
              ? getTaskTitle(task)
              : null
          }
        >
          <span>{getTaskTitle(task)}</span>
        </Tooltip>
      }
      extra={
        !["PENDING", "ON_TRACK"].includes(normalized) ? (
          <StatusBadge variant={mappedStatus.variant}>
            {mappedStatus.label}
          </StatusBadge>
        ) : null
      }
      placement="right"
      onClose={onClose}
      open={open}
      $headerBg={getHeaderBg(task?.status)}
      width="720px"
    >
      {task && (
        <>
          <DrawerContent>
            {youtubeLink && (
              <VideoContainer>
                <VideoIframe
                  title="ChecklistStepVideo"
                  src={`https://www.youtube.com/embed/${youtubeLink}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </VideoContainer>
            )}
            <ScrollingContainer>
              {" "}
              <DrawerSection>
                <DrawerTitle>
                  {getTaskDescription(task)}
                </DrawerTitle>
              </DrawerSection>
              <DrawerSection>
                <DrawerDescription
                  dangerouslySetInnerHTML={{
                    __html:
                      task?.howToDo || (isPt ? task.howToDoPt : task.howToDoEn),
                  }}
                />
              </DrawerSection>
            </ScrollingContainer>

            {downloadLink && (
              <DownloadContainer>
                <a href={downloadLink} target="_blank" rel="noreferrer">
                  <HiOutlineExternalLink
                    color={"rgba(17, 167, 217, 1)"}
                    size={14}
                    style={{ marginRight: 4 }}
                  />
                  {intl.formatMessage({ id: "view_file" })}
                </a>
              </DownloadContainer>
            )}
            <StatusChangeButton onClick={solveTask}>
              {intl.formatMessage(
                task.status.toUpperCase() === "CONCLUDED"
                  ? { id: "checklist.unmark" }
                  : { id: "checklist.solve" }
              )}
            </StatusChangeButton>
          </DrawerContent>
        </>
      )}
    </StyledDrawer>
  );
};

export default ChecklistDrawer;
